import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Truck, Clock, Shield, CheckCircle, Package, ArrowRight, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockStats } from '../mock';
import Tilt from 'react-parallax-tilt';

export const Hero = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect mobile devices
    const checkMobile = () => {
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Only add heavy effects on desktop
    if (!window.matchMedia('(max-width: 768px)').matches) {
      const handleScroll = () => setScrollY(window.scrollY);
      const handleMouseMove = (e) => {
        setMousePosition({
          x: (e.clientX / window.innerWidth - 0.5) * 20,
          y: (e.clientY / window.innerHeight - 0.5) * 20
        });
      };

      window.addEventListener('scroll', handleScroll);
      window.addEventListener('mousemove', handleMouseMove);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('resize', checkMobile);
      };
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const trustIndicators = [
    { icon: Shield, text: '50k£ Goods in Transit Insurance', color: 'from-blue-500 to-blue-600' },
    { icon: Clock, text: 'Same-Day Delivery', color: 'from-emerald-500 to-emerald-600' },
    { icon: CheckCircle, text: '98% On-Time Rate', color: 'from-purple-500 to-purple-600' },
    { icon: Package, text: '15,000+ Deliveries', color: 'from-orange-500 to-orange-600' }
  ];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 overflow-hidden">
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      
      {/* Animated Background Elements with depth - Simplified on mobile */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"
          style={!isMobile ? { transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` } : {}}
        />
        <div 
          className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"
          style={!isMobile ? { transform: `translate(${-mousePosition.x}px, ${mousePosition.y}px)` } : {}}
        />
        <div 
          className="absolute -bottom-8 left-1/2 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"
          style={!isMobile ? { transform: `translate(${mousePosition.y}px, ${-mousePosition.x}px)` } : {}}
        />
      </div>

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-10 w-32 h-32 border-2 border-emerald-500/20 rounded-lg transform rotate-45 animate-float" />
        <div className="absolute bottom-1/3 right-20 w-24 h-24 border-2 border-blue-500/20 rounded-full animate-float-delayed" />
        <div className="absolute top-1/2 right-1/4 w-16 h-16 border-2 border-purple-500/20 transform rotate-12 animate-float-slow" />
      </div>

      {/* Parallax Container with glassmorphism overlay - Disabled on mobile */}
      <div 
        className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        style={!isMobile ? { transform: `translateY(${scrollY * 0.3}px)` } : {}}
      >
        <div className="flex flex-col items-center justify-center min-h-screen py-20 pb-40">
          
          {/* 3D Floating Van Icon - Simplified on mobile */}
          <div 
            className="mb-8 transform-gpu hover:scale-110 transition-transform duration-500"
            style={!isMobile ? { 
              transform: `perspective(1000px) rotateX(${mousePosition.y * 0.5}deg) rotateY(${mousePosition.x * 0.5}deg) translateZ(50px)`,
              transition: 'transform 0.1s ease-out'
            } : {}}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500 blur-2xl opacity-50 rounded-full animate-pulse" />
              <div className="relative bg-gradient-to-br from-emerald-400 to-emerald-600 p-6 rounded-3xl shadow-2xl">
                <Truck className="h-20 w-20 text-white animate-bounce-slow" />
              </div>
            </div>
          </div>

          {/* Main Heading with 3D Effect - SEO Optimized H1 - Simplified on mobile */}
          <div className="text-center mb-8 space-y-4">
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 transform-gpu"
              style={{ 
                textShadow: '0 10px 30px rgba(0,0,0,0.5), 0 0 60px rgba(16,185,129,0.3)',
                transform: !isMobile ? `perspective(1000px) translateZ(${scrollY * 0.1}px)` : 'none'
              }}
            >
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-blue-400 bg-clip-text text-transparent animate-gradient">
                Same Day Delivery Service
              </span>
              <br />
              <span className="text-4xl sm:text-5xl lg:text-6xl">
                Berkshire & Hampshire
              </span>
            </h1>
            <p 
              className="text-xl sm:text-2xl text-emerald-100 max-w-3xl mx-auto leading-relaxed"
              style={!isMobile ? { transform: `perspective(1000px) translateZ(30px)` } : {}}
            >
              Professional B2B courier service with <strong className="text-white">50k£ goods in transit insurance</strong> and <strong className="text-white">98% on-time delivery rate</strong>. Serving businesses across Reading, Slough, Wokingham, Bracknell, Windsor, Maidenhead & beyond.
            </p>
            <p className="text-lg text-emerald-200">
              📞 Call <a href="tel:01344508095" className="text-white font-bold hover:text-emerald-300 transition-colors">01344 508095</a> for instant quotes | ⏰ Available 24/7
            </p>
          </div>

          {/* Glassmorphism CTA Buttons with enhanced 3D effects */}
          <div className="flex flex-col items-center mb-12">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/book')}
                size="lg"
                className="group relative bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 py-6 text-lg font-semibold shadow-2xl transform hover:scale-105 hover:-translate-y-2 transition-all duration-300 overflow-hidden"
                style={{ transform: 'translateZ(30px)' }}
                data-testid="hero-book-now-btn"
              >
                {/* Animated gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity animate-shimmer-button" />
                
                <span className="flex items-center space-x-2 relative z-10">
                  <Package className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                  <span>Book Collection Now</span>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-2 transition-transform" />
                </span>
                
                {/* Glow effect */}
                <div className="absolute inset-0 bg-emerald-400 opacity-0 group-hover:opacity-20 blur-xl transition-opacity" />
              </Button>
              
              <Button
                onClick={() => navigate('/track')}
                size="lg"
                variant="outline"
                className="group relative backdrop-blur-xl bg-white/10 border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 px-8 py-6 text-lg font-semibold shadow-2xl transform hover:scale-105 hover:-translate-y-2 transition-all duration-300 overflow-hidden"
                style={{ transform: 'translateZ(30px)' }}
                data-testid="hero-track-btn"
              >
                {/* Animated border glow */}
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-emerald-500/0 via-emerald-500/50 to-emerald-500/0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ padding: '2px' }} />
                
                <span className="flex items-center space-x-2 relative z-10">
                  <span>Track Order</span>
                </span>
              </Button>
            </div>

            {/* Hint: instant pricing inside booking page */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-emerald-400/30 text-emerald-100 text-sm shadow-lg">
              <Zap className="h-4 w-4 text-emerald-300" />
              <span>
                Instant price estimate available when you click{' '}
                <strong className="text-white">Book Collection Now</strong>
              </span>
            </div>
          </div>

          {/* 3D Trust Indicators with enhanced glassmorphism and tilt - Simplified on mobile */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-6xl">
            {trustIndicators.map((item, index) => {
              const cardContent = (
                <Card
                  className="group backdrop-blur-xl bg-white/10 border border-white/20 hover:bg-white/20 transition-all duration-500 cursor-pointer overflow-hidden relative h-full"
                  style={{
                    transformStyle: isMobile ? 'flat' : 'preserve-3d',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  }}
                >
                  {/* Animated shimmer effect - disabled on mobile */}
                  {!isMobile && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  )}
                  
                  <CardContent className="p-6 text-center relative z-10">
                    <div 
                      className={`flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-gradient-to-br ${item.color} mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}
                      style={!isMobile ? { transform: 'translateZ(40px)' } : {}}
                    >
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <p 
                      className="text-sm font-medium text-white group-hover:text-emerald-300 transition-colors"
                      style={!isMobile ? { transform: 'translateZ(30px)' } : {}}
                    >
                      {item.text}
                    </p>
                  </CardContent>
                </Card>
              );

              // Render with Tilt on desktop, without Tilt on mobile
              return isMobile ? (
                <div key={index} className="transform transition-all duration-500">
                  {cardContent}
                </div>
              ) : (
                <Tilt
                  key={index}
                  className="transform transition-all duration-500"
                  tiltMaxAngleX={15}
                  tiltMaxAngleY={15}
                  perspective={1000}
                  scale={1.05}
                  transitionSpeed={1500}
                  glareEnable={true}
                  glareMaxOpacity={0.3}
                  glareColor="#10b981"
                  glarePosition="all"
                  glareBorderRadius="16px"
                >
                  {cardContent}
                </Tilt>
              );
            })}
          </div>

        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-30px) scale(1.1); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-10deg); }
        }
        
        @keyframes shimmer-button {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
          animation-delay: 2s;
        }
        
        .animate-float-slow {
          animation: float-slow 10s ease-in-out infinite;
          animation-delay: 4s;
        }
        
        .animate-shimmer-button {
          animation: shimmer-button 2s ease-in-out;
        }
        
        /* Mobile optimizations - Disable heavy effects on mobile */
        @media (max-width: 768px) {
          .animate-blob {
            animation: none;
          }
          
          .animate-float,
          .animate-float-delayed,
          .animate-float-slow {
            animation: none;
          }
          
          /* Simplify transforms for better performance */
          .transform-gpu {
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
};